// Shared building blocks for the Government-format register tabs.
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Printer, FileDown, Eye, PenSquare, Trash2 } from "lucide-react";
import { registerBlobUrl, downloadRegisterPDF } from "@/lib/registerPdf";
import DatePicker from "./DatePicker";

export const GOV_FOOTER_LINES = [
  "Circular No. 74207/I.B.1/2013/LSGD, Date: 11.11.2014",
  "G.O.(Rt) No. 717/2026/LSGD, Date: 12.03.2026",
];

/* Screen footer — official references, bottom-right (mirrors PDF footer) */
export function GovFooterNote() {
  return (
    <div className="flex justify-end mt-4" data-testid="gov-register-footer">
      <div className="text-right text-[11px] leading-4 text-muted-foreground border-t border-border pt-2">
        <div>{GOV_FOOTER_LINES[0]}</div>
        <div>{GOV_FOOTER_LINES[1]}</div>
      </div>
    </div>
  );
}

export function useRegisterList(key) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/registers/${key}`);
      setRows(data);
    } catch {
      toast.error("Failed to load register");
    } finally {
      setLoading(false);
    }
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload, editing) => {
    try {
      if (editing?.id) {
        const { data } = await api.patch(`/registers/${key}/${editing.id}`, payload);
        setRows(prev => prev.map(r => r.id === data.id ? data : r));
        toast.success("Entry updated");
      } else {
        const { data } = await api.post(`/registers/${key}`, payload);
        setRows(prev => [...prev, data]);
        toast.success(`Entry added${data.comp_id ? ` · ${data.comp_id}` : ""}`);
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`/registers/${key}/${row.id}`);
      const { data } = await api.get(`/registers/${key}`);
      setRows(data);
      toast.success("Entry deleted");
      return true;
    } catch {
      toast.error("Delete failed");
      return false;
    }
  };

  return { rows, loading, load, save, remove, setRows };
}

/* Official register header card — title left, format number top-right */
export function RegisterHeader({ title, formatNo, localBody, subtitle, testid, children }) {
  return (
    <Card className="p-0 overflow-hidden" data-testid={testid}>
      <div className="p-5 bg-white border-b border-border">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg sm:text-xl font-bold tracking-wide">{title}</h2>
            <p className="text-sm mt-1">
              <span className="font-semibold">Name of Local Body:</span>{" "}
              <span data-testid={`${testid}-local-body`}>{localBody || "—"}</span>
            </p>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <Badge variant="outline" className="font-mono text-[11px] px-2.5 py-1 border-foreground/30" data-testid={`${testid}-format-no`}>
            Format No: {formatNo}
          </Badge>
        </div>
        {children && <div className="mt-4 flex flex-col sm:flex-row gap-2 flex-wrap">{children}</div>}
      </div>
    </Card>
  );
}

export function SearchBox({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative flex-1 min-w-[200px]">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="pl-9 bg-white h-10" data-testid={testid} />
    </div>
  );
}

/* Preview / Print / PDF buttons driven by the two-pass register engine */
export function RegisterPrintActions({ sections, getBundle, filename, testid }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const withBundle = async (fn) => {
    setBusy(true);
    try {
      const bundle = await getBundle();
      fn(bundle);
    } catch {
      toast.error("Could not prepare the register document");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" disabled={busy} data-testid={`${testid}-preview`}
        onClick={() => withBundle(bundle => setPreviewUrl(registerBlobUrl(sections, bundle, { covers: true })))}>
        <Eye className="w-4 h-4 mr-1.5" /> Print Preview
      </Button>
      <Button variant="outline" size="sm" disabled={busy} data-testid={`${testid}-pdf`}
        onClick={() => withBundle(bundle => downloadRegisterPDF(sections, bundle, filename, { covers: true }))}>
        <FileDown className="w-4 h-4 mr-1.5" /> PDF Export
      </Button>
      <PdfPreviewDialog url={previewUrl} onClose={() => setPreviewUrl("")} title={filename.replace(/\.pdf$/i, "")} />
    </>
  );
}

export function PdfPreviewDialog({ url, onClose, title }) {
  // Escape closes the dialog even when the PDF iframe has focus
  useEffect(() => {
    if (!url) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [url, onClose]);
  return (
    <Dialog open={!!url} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="pdf-preview-dialog">
        <DialogHeader>
          <DialogTitle>{title || "Print Preview"}</DialogTitle>
          <DialogDescription>Official register preview — use Print or your browser&apos;s save-as-PDF.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 border border-border rounded overflow-hidden bg-muted/30">
          {url && <iframe src={url} title="Register preview" className="w-full h-full" data-testid="pdf-preview-frame" />}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="pdf-preview-print"
            onClick={() => {
              const frame = document.querySelector('[data-testid="pdf-preview-frame"]');
              if (frame?.contentWindow) frame.contentWindow.print();
              else if (url) window.open(url, "_blank");
            }}>
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Form field helpers */
export function FormField({ label, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

export function AssetPicker({ items, value, onChange, testid }) {
  return (
    <Select value={value || "none"} onValueChange={v => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="bg-white">
        <SelectValue placeholder="Link a hardware asset (optional)" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— Not linked —</SelectItem>
        {(items || []).map(it => (
          <SelectItem key={it.id} value={it.id}>
            {(it.asset_id || it.sl_no) + " · " + (it.item_name || it.subtype || "Asset")}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* Standard data table for register rows */
export function RegisterTable({ columns, rows, loading, onEdit, onDelete, testid, emptyText }) {
  return (
    <Card className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="bg-muted/60 border-b border-border">
            {columns.map(c => (
              <th key={c.key} className="text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-3 py-2.5">{c.label}</th>
            ))}
            <th className="w-20 px-3 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length + 1} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
          ) : !rows.length ? (
            <tr><td colSpan={columns.length + 1} className="px-3 py-8 text-center text-muted-foreground">{emptyText || "No entries recorded yet."}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`${testid}-row-${i}`}>
              {columns.map(c => (
                <td key={c.key} className="px-3 py-2.5 align-top">{c.render ? c.render(r, i) : (r[c.key] || "—")}</td>
              ))}
              <td className="px-3 py-2.5 align-top">
                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(r)} data-testid={`${testid}-edit-${i}`}>
                    <PenSquare className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onDelete(r)} data-testid={`${testid}-delete-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function AddEntryButton({ onClick, label, testid }) {
  return (
    <Button size="sm" onClick={onClick} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid={testid}>
      <Plus className="w-4 h-4 mr-1.5" /> {label || "Add Entry"}
    </Button>
  );
}

export async function fetchBundle() {
  const { data } = await api.get("/registers/bundle");
  return data;
}

/* Single source of truth for the Local Body name shown on screen and
   printed in the PDFs (the bundle derives it from the hardware records). */
export function useLocalBodyName() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.local_body_name || "");
  useEffect(() => {
    fetchBundle()
      .then(b => { if (b.local_body_name) setName(b.local_body_name); })
      .catch(() => {});
  }, []);
  return name;
}



/* Config-driven Add/Edit dialog.
   field: { name, label, type: text|date|textarea|select|asset, options, placeholder, full } */
export function RegisterEntryDialog({ open, title, description, fields, values, onChange, onClose, onSave, assets, testid }) {
  if (!open) return null;
  const set = (name, v) => onChange({ ...values, [name]: v });
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 py-1">
          {fields.map(f => (
            <div key={f.name} className={f.full || f.type === "textarea" ? "sm:col-span-2" : ""}>
              <FormField label={f.label} testid={`${testid}-field-${f.name}`}>
                {f.type === "textarea" ? (
                  <Textarea value={values[f.name] || ""} onChange={e => set(f.name, e.target.value)} placeholder={f.placeholder} rows={2} className="bg-white" data-testid={`${testid}-input-${f.name}`} />
                ) : f.type === "date" ? (
                  <DatePicker value={values[f.name] || ""} onChange={v => set(f.name, v)} testid={`${testid}-input-${f.name}`} />
                ) : f.type === "select" ? (
                  <Select value={values[f.name] || ""} onValueChange={v => set(f.name, v)}>
                    <SelectTrigger className="bg-white" data-testid={`${testid}-input-${f.name}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "asset" ? (
                  <AssetPicker items={assets} value={values[f.name]} onChange={v => set(f.name, v)} testid={`${testid}-input-${f.name}`} />
                ) : (
                  <Input type={f.type || "text"} value={values[f.name] || ""} onChange={e => set(f.name, e.target.value)} placeholder={f.placeholder} className="bg-white" data-testid={`${testid}-input-${f.name}`} />
                )}
              </FormField>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid={`${testid}-save`}>Save Entry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
